// Interactive 3D isometric view of a fence panel or run.
// Uses SVG with a projection matrix + mouse drag for yaw/pitch. No external 3D deps.
import { useState, useRef, useCallback, useMemo } from "react";
import { RotateCcw, Maximize2 } from "lucide-react";
import { getLaserPreset } from "@/lib/laserCutPresets";

// 3D → 2D projection. Given yaw (Y-axis) and pitch (X-axis) in radians, project a 3D point.
function project(x, y, z, yaw, pitch, distance = 4000) {
  // yaw rotates around vertical Y axis
  const cy = Math.cos(yaw), sy = Math.sin(yaw);
  const x1 = x * cy - z * sy;
  const z1 = x * sy + z * cy;
  // pitch rotates around horizontal X axis
  const cp = Math.cos(pitch), sp = Math.sin(pitch);
  const y2 = y * cp - z1 * sp;
  const z2 = y * sp + z1 * cp;
  // simple perspective divide
  const f = distance / (distance + z2);
  return { x: x1 * f, y: -y2 * f, z: z2 };
}

const shade = (hex, factor) => {
  const h = hex.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  const to = (n) => Math.max(0, Math.min(255, Math.round(n * factor)));
  return `rgb(${to(r)}, ${to(g)}, ${to(b)})`;
};

const cuboidFaces = ({ x, y, z, w, h, d }) => [
  // returns quads as arrays of 4 vertices with a `shade` factor
  { name: "front", verts: [[x, y, z], [x + w, y, z], [x + w, y + h, z], [x, y + h, z]], shade: 1.0 },
  { name: "back", verts: [[x, y, z + d], [x + w, y, z + d], [x + w, y + h, z + d], [x, y + h, z + d]], shade: 0.5 },
  { name: "left", verts: [[x, y, z], [x, y, z + d], [x, y + h, z + d], [x, y + h, z]], shade: 0.7 },
  { name: "right", verts: [[x + w, y, z], [x + w, y, z + d], [x + w, y + h, z + d], [x + w, y + h, z]], shade: 0.75 },
  { name: "top", verts: [[x, y, z], [x + w, y, z], [x + w, y, z + d], [x, y, z + d]], shade: 0.9 },
  { name: "bottom", verts: [[x, y + h, z], [x + w, y + h, z], [x + w, y + h, z + d], [x, y + h, z + d]], shade: 0.4 },
];

const projectFace = (face, yaw, pitch, cx, cy, scale) => {
  const pts = face.verts.map(([px, py, pz]) => project(px, py, pz, yaw, pitch));
  const avgZ = pts.reduce((s, p) => s + p.z, 0) / pts.length;
  return {
    shade: face.shade,
    avgZ,
    d: pts.map((p) => `${cx + p.x * scale},${cy + p.y * scale}`).join(" "),
  };
};

export const Blueprint3DView = ({
  mode = "panel", // "panel" | "sketch" | "members"
  panel, settings, points = [], segments = [], members = [],
  colourHex = "#334155", width = "100%", height = 480, testid = "blueprint-3d-view",
}) => {
  const [yaw, setYaw] = useState(-0.55);
  const [pitch, setPitch] = useState(-0.35);
  const [zoom, setZoom] = useState(1);
  const [drag, setDrag] = useState(null);
  const [pinch, setPinch] = useState(null);
  const svgRef = useRef(null);
  const W = 720, H = 460;
  const cx = W / 2, cy = H / 2 + 20;

  const _dist = (a, b) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);

  const onDown = (e) => {
    if (e.touches && e.touches.length === 2) {
      setPinch({ dist: _dist(e.touches[0], e.touches[1]), zoom });
      e.preventDefault?.();
      return;
    }
    const src = e.touches ? e.touches[0] : e;
    setDrag({ x: src.clientX, y: src.clientY, yaw, pitch });
    e.preventDefault?.();
  };
  const onMove = useCallback((e) => {
    if (e.touches && e.touches.length === 2 && pinch) {
      const d = _dist(e.touches[0], e.touches[1]);
      const ratio = d / (pinch.dist || 1);
      setZoom(Math.max(0.4, Math.min(4, pinch.zoom * ratio)));
      e.preventDefault?.();
      return;
    }
    if (!drag) return;
    const src = e.touches ? e.touches[0] : e;
    const dx = src.clientX - drag.x;
    const dy = src.clientY - drag.y;
    setYaw(drag.yaw + dx * 0.008);
    setPitch(Math.max(-1.2, Math.min(0.6, drag.pitch + dy * 0.006)));
  }, [drag, pinch, zoom]);
  const onUp = () => { setDrag(null); setPinch(null); };
  const onWheel = (e) => {
    e.preventDefault();
    setZoom((z) => Math.max(0.4, Math.min(4, z * (e.deltaY < 0 ? 1.1 : 0.9))));
  };
  const reset = () => { setYaw(-0.55); setPitch(-0.35); setZoom(1); };

  const boxes = useMemo(() => {
    if (mode === "panel" && panel) return panelBoxes(panel);
    if (mode === "members") return membersBoxes(members);
    return sketchBoxes(points, segments, settings);
  }, [mode, panel, points, segments, members, settings]);

  const scale = useMemo(() => {
    // fit bounding box of boxes into view
    if (!boxes.length) return 0.05;
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (const b of boxes) {
      for (const face of cuboidFaces(b)) {
        for (const [x, y, z] of face.verts) {
          const p = project(x, y, z, yaw, pitch);
          minX = Math.min(minX, p.x); maxX = Math.max(maxX, p.x);
          minY = Math.min(minY, p.y); maxY = Math.max(maxY, p.y);
        }
      }
    }
    const sw = maxX - minX, sh = maxY - minY;
    if (sw <= 0 || sh <= 0) return 0.05;
    return 0.85 * Math.min((W - 40) / sw, (H - 60) / sh) * zoom;
  }, [boxes, yaw, pitch, zoom]);

  const faces = useMemo(() => {
    const out = [];
    for (const box of boxes) {
      const isPattern = !!box.patternId;
      for (const face of cuboidFaces(box)) {
        const projected = projectFace(face, yaw, pitch, cx, cy, scale);
        const fillColour = isPattern && face.name === "front"
          ? `url(#${box.patternId})`
          : shade(box.colour || colourHex, projected.shade);
        out.push({
          d: projected.d,
          avgZ: projected.avgZ,
          fill: fillColour,
          strokeShade: projected.shade,
          faceName: face.name,
        });
      }
    }
    return out.sort((a, b) => b.avgZ - a.avgZ);
  }, [boxes, yaw, pitch, scale, cx, cy, colourHex]);

  // laser cut pattern defs (front face only, if present)
  const patternDefs = useMemo(() => {
    const defs = [];
    for (const b of boxes) {
      if (!b.patternId) continue;
      const preset = getLaserPreset(b.laserCutPreset);
      const tile = 40;
      defs.push(
        <pattern key={b.patternId} id={b.patternId} x="0" y="0" width={tile} height={tile} patternUnits="userSpaceOnUse">
          <rect width={tile} height={tile} fill="#f8fafc" />
          {b.laserCutPreset === "custom" && b.laserCutUploadUrl ? (
            <image href={b.laserCutUploadUrl} x="0" y="0" width={tile} height={tile} preserveAspectRatio="xMidYMid slice" />
          ) : (
            <g transform={`scale(${tile / 100})`} dangerouslySetInnerHTML={{ __html: preset.render(b.colour || colourHex) }} />
          )}
        </pattern>
      );
    }
    return defs;
  }, [boxes, colourHex]);

  return (
    <div className="relative select-none rounded-sm bg-gradient-to-b from-slate-100 via-slate-200 to-slate-300 industrial-shadow overflow-hidden"
      style={{ width, height }} data-testid={testid}>
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} className="w-full h-full cursor-grab active:cursor-grabbing touch-none"
        onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}
        onTouchStart={onDown} onTouchMove={onMove} onTouchEnd={onUp} onTouchCancel={onUp}
        onWheel={onWheel}
        style={{ touchAction: "none" }}>
        <defs>
          {patternDefs}
          <linearGradient id="sky3d" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#e0f2fe" />
            <stop offset="100%" stopColor="#cbd5e1" />
          </linearGradient>
        </defs>
        <rect width={W} height={H} fill="url(#sky3d)" />
        {/* ground plane grid */}
        {(() => {
          const gridLines = [];
          const gridSize = 8000;
          const step = 500;
          for (let i = -gridSize; i <= gridSize; i += step) {
            const p1 = project(i, 0, -gridSize, yaw, pitch);
            const p2 = project(i, 0, gridSize, yaw, pitch);
            gridLines.push(<line key={`gv${i}`} x1={cx + p1.x * scale} y1={cy + p1.y * scale}
              x2={cx + p2.x * scale} y2={cy + p2.y * scale} stroke="#94a3b8" strokeWidth={i === 0 ? 1 : 0.4} opacity={0.5} />);
            const p3 = project(-gridSize, 0, i, yaw, pitch);
            const p4 = project(gridSize, 0, i, yaw, pitch);
            gridLines.push(<line key={`gh${i}`} x1={cx + p3.x * scale} y1={cy + p3.y * scale}
              x2={cx + p4.x * scale} y2={cy + p4.y * scale} stroke="#94a3b8" strokeWidth={i === 0 ? 1 : 0.4} opacity={0.5} />);
          }
          return gridLines;
        })()}
        {/* faces */}
        {faces.map((f, i) => (
          <polygon key={i} points={f.d} fill={f.fill}
            stroke={shade(colourHex, Math.max(0.15, f.strokeShade - 0.25))} strokeWidth={0.5}
            strokeLinejoin="round" />
        ))}
      </svg>
      <div className="absolute top-2 right-2 flex gap-1.5">
        <button type="button" onClick={reset} title="Reset view"
          className="rounded-sm bg-white/90 hover:bg-white border border-slate-200 h-8 w-8 flex items-center justify-center text-slate-600"
          data-testid={`${testid}-reset`}>
          <RotateCcw size={13} />
        </button>
      </div>
      <div className="absolute bottom-2 left-2 rounded-sm bg-white/80 text-[10px] text-slate-600 px-2 py-1 font-mono flex items-center gap-1.5">
        <Maximize2 size={9} /> Drag to rotate · Pinch/scroll to zoom · {(yaw * (180 / Math.PI)).toFixed(0)}° / {(pitch * (180 / Math.PI)).toFixed(0)}° · {(zoom * 100).toFixed(0)}%
      </div>
    </div>
  );
};

// ---- Build cuboid list for each mode ----
function panelBoxes(p) {
  if (!p) return [];
  const W = p.width_mm || 2400, H = p.height_mm || 1800, D = p.post_size_mm || 65;
  const postW = p.kind === "box" ? 60 : 60;
  const railH = 40;
  const postDepth = D;
  const railDepth = D * 0.6;
  const y = H; // ground=0, top of fence = H flipped later; we use y down = up-negative when projecting
  const cx0 = -W / 2;
  const boxes = [];

  if (p.kind === "box") {
    // Enclosure — 6 walls
    const wall = 20;
    boxes.push({ x: cx0, y: -H, z: 0, w: W, h: H, d: wall, colour: p.colour_hex });
    boxes.push({ x: cx0, y: -H, z: (p.depth_mm || 600) - wall, w: W, h: H, d: wall, colour: p.colour_hex });
    boxes.push({ x: cx0, y: -H, z: wall, w: wall, h: H, d: (p.depth_mm || 600) - 2 * wall, colour: p.colour_hex });
    boxes.push({ x: cx0 + W - wall, y: -H, z: wall, w: wall, h: H, d: (p.depth_mm || 600) - 2 * wall, colour: p.colour_hex });
    boxes.push({ x: cx0, y: -H, z: 0, w: W, h: wall, d: (p.depth_mm || 600), colour: p.colour_hex });
    return boxes;
  }

  // Two posts
  boxes.push({ x: cx0, y: -H, z: 0, w: postW, h: H, d: postDepth, colour: p.colour_hex });
  boxes.push({ x: cx0 + W - postW, y: -H, z: 0, w: postW, h: H, d: postDepth, colour: p.colour_hex });
  // Top + bottom rails
  const railZ = (postDepth - railDepth) / 2;
  boxes.push({ x: cx0 + postW, y: -H, z: railZ, w: W - 2 * postW, h: railH, d: railDepth, colour: p.colour_hex });
  boxes.push({ x: cx0 + postW, y: -railH, z: railZ, w: W - 2 * postW, h: railH, d: railDepth, colour: p.colour_hex });
  // Mid rails
  for (let i = 0; i < (parseInt(p.mid_rails) || 0); i++) {
    const my = -H + ((i + 1) * H) / ((parseInt(p.mid_rails) || 0) + 1) - railH / 2;
    boxes.push({ x: cx0 + postW, y: my, z: railZ, w: W - 2 * postW, h: railH, d: railDepth, colour: p.colour_hex });
  }
  const clearW = W - 2 * postW;
  const innerY0 = -H + railH;
  const innerH = H - 2 * railH;
  const tubeD = 22;

  if (p.infill_type === "tube_spike" || p.infill_type === "tube_flat") {
    const tubeSize = 25;
    const spike = p.infill_type === "tube_spike" ? (p.spike_extension_mm || 0) : 0;
    const centres = Math.max(tubeSize + 5, p.tube_centres_mm || 50);
    const n = Math.max(2, Math.floor((clearW - tubeSize) / centres) + 1);
    for (let i = 0; i < n; i++) {
      const tx = cx0 + postW + i * ((clearW - tubeSize) / (n - 1));
      boxes.push({ x: tx, y: -H - spike, z: railZ + (railDepth - tubeD) / 2, w: tubeSize, h: H - railH + spike, d: tubeD, colour: p.colour_hex });
      if (spike) {
        // spike tip cube
        boxes.push({ x: tx, y: -H - spike - 12, z: railZ + (railDepth - tubeD) / 2, w: tubeSize, h: 12, d: tubeD, colour: p.colour_hex });
      }
    }
  } else if (p.infill_type === "slat") {
    const sw = p.slat_width_mm || 65;
    const gap = p.slat_gap_mm || 10;
    const n = Math.max(1, Math.floor((innerH + gap) / (sw + gap)));
    const pitch = n > 1 ? (innerH - n * sw) / (n - 1) + sw : innerH;
    for (let i = 0; i < n; i++) {
      const sy = innerY0 + i * pitch;
      boxes.push({ x: cx0 + postW, y: sy, z: railZ + railDepth / 2 - 6, w: clearW, h: sw, d: 12, colour: p.colour_hex });
    }
  } else if (p.infill_type === "louvre") {
    const lw = p.louvre_width_mm || 100;
    const gap = p.louvre_gap_mm || 20;
    const angle = (p.louvre_angle_deg || 45) * Math.PI / 180;
    const projected = lw * Math.cos(angle);
    const pitch = Math.max(1, projected + gap);
    const n = Math.max(1, Math.floor(innerH / pitch));
    // Louvres: represent each as thin slab; angle approximated by depth + Y offset
    for (let i = 0; i < n; i++) {
      const sy = innerY0 + (i + 0.5) * (innerH / n);
      const depthOffset = lw * Math.sin(angle);
      boxes.push({ x: cx0 + postW, y: sy - projected / 2, z: railZ + railDepth / 2 - depthOffset / 2, w: clearW, h: projected, d: Math.max(10, depthOffset), colour: p.colour_hex });
    }
  } else if (p.infill_type === "blade") {
    const bw = p.blade_width_mm || 50;
    const bt = p.blade_thickness_mm || 6;
    const gap = p.blade_gap_mm || 20;
    if (p.blade_orientation === "horizontal") {
      const n = Math.max(1, Math.floor((innerH + gap) / (bw + gap)));
      const pitch = n > 1 ? (innerH - n * bw) / (n - 1) + bw : innerH;
      for (let i = 0; i < n; i++) {
        boxes.push({ x: cx0 + postW, y: innerY0 + i * pitch, z: railZ + (railDepth - bt) / 2, w: clearW, h: bw, d: bt, colour: p.colour_hex });
      }
    } else {
      const n = Math.max(1, Math.floor((clearW + gap) / (bw + gap)));
      const pitch = n > 1 ? (clearW - n * bw) / (n - 1) + bw : clearW;
      for (let i = 0; i < n; i++) {
        boxes.push({ x: cx0 + postW + i * pitch, y: innerY0, z: railZ + (railDepth - bt) / 2, w: bw, h: innerH, d: bt, colour: p.colour_hex });
      }
    }
  } else if (p.infill_type === "laser_cut") {
    // Sheet with pattern on front face
    const thk = Math.max(2, p.laser_cut_sheet_thickness_mm || 3);
    boxes.push({
      x: cx0 + postW, y: innerY0, z: railZ + (railDepth - thk) / 2,
      w: clearW, h: innerH, d: thk, colour: p.colour_hex,
      patternId: `p3d-laser-${p.laser_cut_preset || "default"}`,
      laserCutPreset: p.laser_cut_preset,
      laserCutUploadUrl: p.laser_cut_upload_url,
    });
  } else if (["rolltop", "anticlimb", "mesh"].includes(p.infill_type)) {
    boxes.push({ x: cx0 + postW, y: innerY0, z: railZ + (railDepth - 3) / 2, w: clearW, h: innerH, d: 3, colour: p.colour_hex });
  } else if (p.infill_type === "sheet") {
    boxes.push({ x: cx0 + postW, y: innerY0, z: railZ + (railDepth - 5) / 2, w: clearW, h: innerH, d: 5, colour: p.colour_hex });
  }
  return boxes;
}

function sketchBoxes(points, segments, settings) {
  if (!points || points.length < 2) return [];
  const s = settings || {};
  const H = s.height_mm || 1800;
  const scale = 30; // 1 grid unit ≈ 30mm — pretty enough for 3D
  const boxes = [];
  const postW = s.post_size_mm || 65;
  const infill = s.infill || "pickets";
  const railH = 40;
  // find bounding box for centering
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const p of points) {
    minX = Math.min(minX, p.x); maxX = Math.max(maxX, p.x);
    minY = Math.min(minY, p.y); maxY = Math.max(maxY, p.y);
  }
  const ox = (minX + maxX) / 2, oy = (minY + maxY) / 2;

  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i], b = points[i + 1];
    const meta = segments[i] || { type: "fence" };
    const dx = (b.x - a.x) * scale, dz = (b.y - a.y) * scale;
    const lenMm = Math.hypot(dx, dz);
    const angle = Math.atan2(dz, dx);
    if (meta.type === "gate" || lenMm < 100) continue;

    const ax = (a.x - ox) * scale, az = (a.y - oy) * scale;
    // Break into short segments each posts.length apart, each an axis-aligned box rotated by angle later — for simplicity, project as many small aligned boxes
    // Use small step (600mm) placements along run so orientation renders as staircase
    const stepMm = 600;
    const nSteps = Math.max(1, Math.ceil(lenMm / stepMm));
    const stepX = dx / nSteps, stepZ = dz / nSteps;
    // Posts along run
    for (let k = 0; k <= nSteps; k++) {
      const px = ax + k * stepX, pz = az + k * stepZ;
      boxes.push({ x: px - postW / 2, y: -H - 200, z: pz - postW / 2, w: postW, h: H + 200, d: postW, colour: settings?.colour_hex });
    }
    // Top rail (approximated as chain of small boxes)
    for (let k = 0; k < nSteps; k++) {
      const px = ax + k * stepX, pz = az + k * stepZ;
      const nx = ax + (k + 1) * stepX, nz = az + (k + 1) * stepZ;
      const mx = (px + nx) / 2, mz = (pz + nz) / 2;
      // width across the run tangent — we'll just use the actual segment length as box width (approximate box aligned along the segment)
      const segLen = Math.hypot(stepX, stepZ);
      // We fake orientation by a wide box aligned to the average — good-enough for 3D preview
      const w = segLen;
      const d = 25;
      const boxX = mx - w / 2;
      const boxZ = mz - d / 2;
      // top rail
      boxes.push({ x: boxX, y: -H, z: boxZ, w, h: railH, d, colour: settings?.colour_hex });
      // bottom rail
      boxes.push({ x: boxX, y: -railH, z: boxZ, w, h: railH, d, colour: settings?.colour_hex });
      // infill — pickets or slats simplified
      if (infill === "pickets") {
        const nP = Math.max(2, Math.floor(w / 100));
        for (let j = 0; j < nP; j++) {
          const px2 = boxX + 20 + (j * (w - 40)) / (nP - 1 || 1);
          boxes.push({ x: px2 - 8, y: -H + railH, z: boxZ + d / 2 - 8, w: 16, h: H - 2 * railH, d: 16, colour: settings?.colour_hex });
        }
      } else if (infill === "slats" || infill === "louvre") {
        const nS = 8;
        for (let j = 0; j < nS; j++) {
          boxes.push({ x: boxX, y: -H + railH + (j * (H - 2 * railH)) / nS, z: boxZ + d / 2 - 6, w, h: 40, d: 12, colour: settings?.colour_hex });
        }
      } else if (infill === "blade") {
        const nB = 10;
        for (let j = 0; j < nB; j++) {
          const bx = boxX + (j * w) / nB;
          boxes.push({ x: bx, y: -H + railH, z: boxZ + d / 2 - 3, w: w / nB * 0.5, h: H - 2 * railH, d: 6, colour: settings?.colour_hex });
        }
      } else if (infill === "mesh") {
        boxes.push({ x: boxX, y: -H + railH, z: boxZ + d / 2 - 1, w, h: H - 2 * railH, d: 2, colour: settings?.colour_hex });
      }
    }
  }
  return boxes;
}

function membersBoxes(members) {
  if (!members || members.length === 0) return [];
  // Simplified: stack members left→right as a single panel like the elevation view
  const boxes = [];
  const totalWmm = 2400, totalHmm = 1800, depth = 60;
  const cx0 = -totalWmm / 2;
  const posts = members.filter((m) => m.kind === "post");
  const rails = members.filter((m) => m.kind === "rail");
  const slats = members.filter((m) => m.kind === "slat");
  const tubes = members.filter((m) => m.kind === "tube");
  const louvres = members.filter((m) => m.kind === "louvre");
  const blades = members.filter((m) => m.kind === "blade");

  const expand = (list) => list.flatMap((m) => Array.from({ length: parseInt(m.qty) || 1 }, () => m));
  const ePosts = expand(posts), eRails = expand(rails), eSlats = expand(slats), eTubes = expand(tubes), eLouvres = expand(louvres), eBlades = expand(blades);

  ePosts.forEach((m, i) => {
    const isL = m.position === "left" || (!m.position && i === 0);
    const isR = m.position === "right" || (!m.position && i === ePosts.length - 1);
    const x = isL ? cx0 : isR ? cx0 + totalWmm - 60 : cx0 + 60 + (i * (totalWmm - 120)) / Math.max(1, ePosts.length - 1);
    boxes.push({ x, y: -(m.length_mm || totalHmm), z: 0, w: 60, h: m.length_mm || totalHmm, d: 60, colour: undefined });
  });
  eRails.forEach((m, i) => {
    const y = m.position === "top" ? -totalHmm : m.position === "bottom" ? -40 : -totalHmm + ((i + 1) * totalHmm) / (eRails.length + 1);
    boxes.push({ x: cx0 + 60, y, z: 15, w: m.length_mm || totalWmm - 120, h: 40, d: 30 });
  });
  eSlats.forEach((m, i) => {
    if (m.position === "vertical") {
      const x = cx0 + 60 + (i * (totalWmm - 120)) / Math.max(1, eSlats.length - 1);
      boxes.push({ x, y: -totalHmm + 40, z: 24, w: 65, h: m.length_mm || totalHmm - 80, d: 12 });
    } else {
      const y = -totalHmm + 40 + (i * (totalHmm - 80)) / Math.max(1, eSlats.length);
      boxes.push({ x: cx0 + 60, y, z: 24, w: m.length_mm || totalWmm - 120, h: 65, d: 12 });
    }
  });
  eLouvres.forEach((m, i) => {
    const y = -totalHmm + 40 + (i * (totalHmm - 80)) / Math.max(1, eLouvres.length);
    boxes.push({ x: cx0 + 60, y, z: 15, w: m.length_mm || totalWmm - 120, h: 40, d: 40 });
  });
  eBlades.forEach((m, i) => {
    if (m.position === "horizontal") {
      const y = -totalHmm + 40 + (i * (totalHmm - 80)) / Math.max(1, eBlades.length);
      boxes.push({ x: cx0 + 60, y, z: 24, w: m.length_mm || totalWmm - 120, h: 50, d: 6 });
    } else {
      const x = cx0 + 60 + (i * (totalWmm - 120)) / Math.max(1, eBlades.length - 1);
      boxes.push({ x, y: -totalHmm + 40, z: 24, w: 50, h: m.length_mm || totalHmm - 80, d: 6 });
    }
  });
  eTubes.forEach((m, i) => {
    const x = cx0 + 60 + (i * (totalWmm - 120)) / Math.max(1, eTubes.length - 1);
    boxes.push({ x, y: -totalHmm + 40, z: 22, w: 25, h: m.length_mm || totalHmm - 80, d: 25 });
  });
  return boxes;
}

export default Blueprint3DView;
